import React, { useState, useEffect, useRef } from 'react';
import {
  DragDropContext,
  DropResult,
  DraggableLocation
} from 'react-beautiful-dnd';
import { StateNodeConfig, WorkflowInvoke } from '@sierralabs/workflow';

import * as Styled from './board/BoardPage.styled';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import BoardColumn from './board/BoardColumn';
import { mapTaskByStatus, createEmptyTaskMap } from '../tasks/task.helper';
import { sample as workflowData } from './training-workflow-sample';
import { tasks as mockTasks } from './training-task-sample';
import { Task } from '../tasks/Task.interface';
import TrainingCreateSubTaskDialog from './dialogs/TrainingCreateSubTask';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';

const reorder = (task: Task[], startIndex: number, endIndex: number) => {
  const result = Array.from(task);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const move = (
  source: Task[],
  destination: Task[],
  droppableSource: DraggableLocation,
  droppableDestination: DraggableLocation
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {} as any;
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const TrainingAdminBoard: React.FC = () => {
  const lastSourceRef = useRef<DraggableLocation | undefined>();
  const lastDestinationRef = useRef<DraggableLocation | undefined>();
  const [workflow, setWorkflow] = useState<any>([]);
  const [taskMap, setTaskMap] = useState<any>();
  const [selectedTask, setSelectedTask] = useState<any>();
  const [openCreateSubTask, setOpenCreateSubTask] = useState(false);
  const [openConfirmRemoveSubTasks, setOpenConfirmRemoveSubTasks] = useState(
    false
  );
  console.log('workflowData', workflowData);
  useEffect(() => {
    const { states } = workflowData;
    setWorkflow(states);

    if (mockTasks) {
      setTaskMap(mapTaskByStatus(states, mockTasks || []));
    } else {
      setTaskMap({ ...createEmptyTaskMap(states) });
    }
  }, []);

  const handleUpdateTaskStatus = (isRevert = false) => {
    const { current: lastSource } = lastSourceRef;
    const { current: lastDestination } = lastDestinationRef;

    if (!lastSource || !lastDestination) return;

    const source: StateNodeConfig = workflow[parseInt(lastSource.droppableId)];
    const destination: StateNodeConfig =
      workflow[parseInt(lastDestination.droppableId)];
    const task = taskMap[source.name][lastSource.index];

    if (!task) return;

    // TODO: make dynamic from workflow
    if (source.name === 'To Do' && !['Draft'].includes(destination.name)) {
      return;
    } else if (
      source.name === 'Draft' &&
      !['In Review', 'To Do'].includes(destination.name)
    ) {
      return;
    } else if (
      source.name === 'Approved' &&
      !['To Do'].includes(destination.name)
    ) {
      return;
    }

    task.status = destination.name; // update the status in the task
    const result = move(
      taskMap[source.name],
      taskMap[destination.name],
      lastSource,
      lastDestination
    );
    setTaskMap({
      ...taskMap,
      [source.name]: result[parseInt(lastSource.droppableId)],
      [destination.name]: result[parseInt(lastDestination.droppableId)]
    });

    // swap source and destination for revert
    lastSourceRef.current = lastDestination;
    lastDestinationRef.current = lastSource;
    const invoke: WorkflowInvoke[] | any = Array.isArray(destination.invoke)
      ? destination.invoke
      : [destination.invoke];
    if (
      !isRevert &&
      invoke?.some((invoke: WorkflowInvoke) => invoke.src === 'createSubTask')
    ) {
      setSelectedTask(task);
      setOpenCreateSubTask(true);
      return;
    }
    if (
      !isRevert &&
      source.name === 'In Review' &&
      ['Draft', 'To Do'].includes(destination.name)
    ) {
      setSelectedTask(task);
      setOpenConfirmRemoveSubTasks(true);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }

    lastSourceRef.current = source;
    lastDestinationRef.current = destination;

    if (source.droppableId === destination.droppableId) {
      const column: StateNodeConfig = workflow[parseInt(source.droppableId)];
      const items = reorder(
        taskMap[column.name],
        source.index,
        destination.index
      );
      setTaskMap({ ...taskMap, [column.name]: items });
    } else {
      handleUpdateTaskStatus();
    }
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Training Admin Board</h2>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Styled.Board>
            {workflow?.map((state: StateNodeConfig, index: number) => (
              <BoardColumn
                key={index}
                title={state.name}
                tasks={taskMap[state.name]}
                droppableId={index.toString()}
              />
            ))}
          </Styled.Board>
        </DragDropContext>
      </PageTemplateDashboard>
      {openCreateSubTask && (
        <TrainingCreateSubTaskDialog
          trainingId={selectedTask.id}
          open={openCreateSubTask}
          onCancel={() => {
            setOpenCreateSubTask(false);
            handleUpdateTaskStatus(true);
          }}
          onSubTaskCreated={() => {
            setTaskMap({ ...taskMap });
            setOpenCreateSubTask(false);
          }}
        />
      )}
      {openConfirmRemoveSubTasks && (
        <ConfirmDialog
          open={openConfirmRemoveSubTasks}
          title='Remove Reviewers'
          message='Are you sure you want to remove the reviewers from the task?'
          onCancel={() => {
            setOpenConfirmRemoveSubTasks(false);
            handleUpdateTaskStatus(true);
          }}
          onConfirm={() => {
            if (selectedTask) {
              selectedTask.children = [];
            }
            setOpenConfirmRemoveSubTasks(false);
          }}
        />
      )}
    </PageTemplate>
  );
};

export default TrainingAdminBoard;
